import React, { useEffect, useState, useRef } from "react";
import { api, fmtPrice, mediaUrl, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { statusLabel, statusColor } from "@/lib/status";
import { Link } from "react-router-dom";
import { Camera, Loader2, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const { t } = useLang();
  const [orders, setOrders] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const NPAGE = 5;
  const [notifs, setNotifs] = useState({ items: [], total: 0, unread: 0 });
  const [nPage, setNPage] = useState(1);
  const loadNotifs = (p) => api.get(`/notifications?page=${p}&page_size=${NPAGE}`).then((r) => setNotifs(r.data)).catch(() => {});
  const goNotif = (p) => { setNPage(p); loadNotifs(p); };
  const notifPages = Math.max(1, Math.ceil(notifs.total / NPAGE));

  useEffect(() => { api.get("/orders").then((r) => setOrders(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadNotifs(1).then(() => api.post("/notifications/read").catch(() => {})); }, []);

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put("/auth/profile", { avatar: data.url });
      await refreshUser();
      toast.success(t("photo_updated"));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-display font-extrabold text-3xl tracking-tight mb-8">{t("profile")}</h1>
      <div className="bg-white border border-stone-200 rounded-xl p-6 flex items-center gap-5 mb-8">
        <div className="relative shrink-0">
          <span className="w-20 h-20 rounded-full bg-emerald-800 text-white grid place-items-center text-2xl font-display font-extrabold overflow-hidden">
            {user?.avatar ? (
              <img src={mediaUrl(user.avatar)} alt={user?.name} className="w-full h-full object-cover" data-testid="profile-avatar-img" />
            ) : (
              user?.name?.[0]?.toUpperCase()
            )}
          </span>
          <input ref={fileRef} type="file" accept="image/*" onChange={uploadAvatar} className="hidden" data-testid="avatar-input" />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            title={t("change_photo")}
            data-testid="avatar-change-btn"
            className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-mango-600 hover:bg-mango-700 text-white grid place-items-center shadow-md ring-2 ring-white transition-colors disabled:opacity-70"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          </button>
        </div>
        <div>
          <p className="font-display font-bold text-xl" data-testid="profile-name">{user?.name}</p>
          <p className="text-stone-500 text-sm">{user?.email}</p>
          <p className="text-stone-500 text-sm">{user?.phone}</p>
          <span className="inline-block mt-2 text-xs font-semibold px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 capitalize">{user?.role}</span>
        </div>
      </div>

      <h2 className="font-display font-bold text-lg mb-4">{t("recent_orders")}</h2>
      <div className="space-y-2">
        {orders.slice(0, 5).map((o) => (
          <Link to="/orders" key={o.id} className="flex items-center justify-between bg-white border border-stone-200 rounded-lg p-4 hover:border-emerald-800 transition-colors">
            <div><p className="font-medium text-sm">{o.order_no}</p><p className="text-xs text-stone-400">{o.items.length} {t("items")}</p></div>
            <div className="flex items-center gap-3">
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusColor(o.status)}`}>{statusLabel(t, o.status)}</span>
              <span className="font-semibold text-emerald-800 text-sm">{fmtPrice(o.total)}</span>
            </div>
          </Link>
        ))}
        {orders.length === 0 && <p className="text-stone-400 text-sm">{t("no_orders")}</p>}
      </div>

      <h2 className="font-display font-bold text-lg mb-4 mt-10 flex items-center gap-2"><Bell className="w-5 h-5" />Notifications</h2>
      <div className="space-y-2" data-testid="notifications-list">
        {notifs.items.length === 0 && <p className="text-stone-400 text-sm" data-testid="no-notifications">Aucune notification</p>}
        {notifs.items.map((n) => (
          <div key={n.id} className={`border rounded-lg p-4 ${n.read ? "bg-white border-stone-200" : "bg-emerald-50/50 border-emerald-300"}`} data-testid={`notif-${n.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-sm">{n.title}</p>
                <p className="text-sm text-stone-600">{n.message}</p>
              </div>
              {!n.read && <span className="shrink-0 mt-1.5 w-2 h-2 rounded-full bg-emerald-600" />}
            </div>
            <p className="text-xs text-stone-400 mt-2">{new Date(n.created_at).toLocaleString()}</p>
          </div>
        ))}
      </div>
      {notifs.total > NPAGE && (
        <div className="flex items-center justify-between mt-4" data-testid="notif-pagination">
          <p className="text-xs text-stone-400">Affichage {(nPage - 1) * NPAGE + 1}–{Math.min(nPage * NPAGE, notifs.total)} sur {notifs.total}</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-full" disabled={nPage <= 1} onClick={() => goNotif(nPage - 1)} data-testid="notif-prev">Précédent</Button>
            <span className="text-xs font-semibold" data-testid="notif-page">Page {nPage} / {notifPages}</span>
            <Button variant="outline" size="sm" className="rounded-full" disabled={nPage >= notifPages} onClick={() => goNotif(nPage + 1)} data-testid="notif-next">Suivant</Button>
          </div>
        </div>
      )}
    </div>
  );
}
